import {
  type AnalyticsConsentState,
  saveAnalyticsConsentState,
} from '@/analytics/analytics-consent';
import { resolveGoogleAnalyticsMeasurementId } from '@/analytics/analytics-runtime-config';

type GoogleAnalyticsEventProperties = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[][];
    gtag?: GoogleAnalyticsGtagFn;
  }
}

type GoogleAnalyticsGtagFn = (
  command: string,
  target: Date | Record<string, unknown> | string,
  params?: Record<string, unknown>
) => void;

function isBrowser() {
  return typeof window !== 'undefined';
}

function ensureGoogleAnalyticsStub() {
  if (!isBrowser()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
}

export function applyGoogleAnalyticsConsent(params: {
  analyticsStorage: AnalyticsConsentState;
  mode: 'default' | 'update';
}) {
  if (!isBrowser() || !resolveGoogleAnalyticsMeasurementId()) {
    return;
  }

  ensureGoogleAnalyticsStub();
  window.gtag?.('consent', params.mode, {
    analytics_storage: params.analyticsStorage,
  });
}

export function dispatchGoogleAnalyticsEvent(
  event: string,
  properties?: GoogleAnalyticsEventProperties
) {
  if (!isBrowser() || !resolveGoogleAnalyticsMeasurementId()) {
    return;
  }

  ensureGoogleAnalyticsStub();
  window.gtag?.('event', event, properties);
}

export function setGoogleAnalyticsUserId(userId: string | null) {
  if (!isBrowser() || !resolveGoogleAnalyticsMeasurementId()) {
    return;
  }

  ensureGoogleAnalyticsStub();
  window.gtag?.('set', { user_id: userId });
}

export function updateGoogleAnalyticsConsent(
  analyticsStorage: AnalyticsConsentState
) {
  saveAnalyticsConsentState(analyticsStorage);
  applyGoogleAnalyticsConsent({
    analyticsStorage,
    mode: 'update',
  });
}
